var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { ELocale } from "../../types/i18n.types";
import { stringToSnakeCase } from "../string.utils";
import { capitalize } from "../text/text.utils";
import MESSAGES_OVERRIDES from "./locale/i18n.overrides.json";
var MESSAGES_EN = {};
var MESSAGES_NL = {};
var MESSAGES_FR = {};
// Each app passes its own translations at start (its project's translations.json),
// so no app bundles the keys of the others. The overrides are merged last and win;
// _meta is their metadata, not a key. Until this runs, every key renders as its id.
export var setTranslations = function () {
    var files = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        files[_i] = arguments[_i];
    }
    [MESSAGES_EN, MESSAGES_NL, MESSAGES_FR].forEach(function (messages) {
        Object.keys(messages).forEach(function (key) { return delete messages[key]; });
    });
    var merged = Object.assign.apply(Object, __spreadArray(__spreadArray([{}], files, false), [MESSAGES_OVERRIDES], false));
    Object.keys(merged).forEach(function (key) {
        if (key === "_meta")
            return;
        var values = merged[key];
        if (!Array.isArray(values))
            return;
        var enMessage = values[0], nlMessage = values[1], frMessage = values[2];
        if (enMessage) {
            MESSAGES_EN[key] = enMessage;
        }
        if (nlMessage) {
            MESSAGES_NL[key] = nlMessage;
        }
        if (frMessage) {
            MESSAGES_FR[key] = frMessage;
        }
    });
};
// The overrides apply from the start, also in code that never sets translations (tests).
setTranslations();
var DEFAULT_LOCALE = ELocale.EN;
var MESSAGES = (_a = {},
    _a[ELocale.EN] = MESSAGES_EN,
    _a[ELocale.NL] = MESSAGES_NL,
    _a[ELocale.FR] = MESSAGES_FR,
    _a[ELocale.INTERNAL] = {},
    _a);
var locale = DEFAULT_LOCALE;
export var getLocale = function () { return locale; };
var rawLocale = DEFAULT_LOCALE;
export var getRawLocale = function () { return rawLocale; };
export var setMappedLocale = function (newLocale) {
    locale = newLocale;
};
// A language as the app writes it (nl_BE, fr_FR, fr_BE, en_UK, "internal")
// as the locale its texts live in, or undefined for one we do not translate.
// Who decides what that means is the caller's business: `setLocale` reads the
// broker's own language and falls back to English, while a text addressed to a
// contact leaves undefined alone and follows the reader instead.
export var toLocale = function (language) {
    var _a;
    if (language === ELocale.INTERNAL)
        return ELocale.INTERNAL;
    var lower = (_a = language === null || language === void 0 ? void 0 : language.toLowerCase()) !== null && _a !== void 0 ? _a : "";
    if (lower.includes("nl"))
        return ELocale.NL;
    if (lower.includes("fr"))
        return ELocale.FR;
    if (lower.includes("en"))
        return ELocale.EN;
    return undefined;
};
export var setLocale = function (newLocale) {
    var _a;
    rawLocale = newLocale;
    locale = (_a = toLocale(newLocale)) !== null && _a !== void 0 ? _a : ELocale.EN;
};
var getMessagesForLocale = function (locale) {
    return MESSAGES[locale];
};
var getMessageForId = function (id, pluralIndex, customLocale) {
    if (pluralIndex === void 0) { pluralIndex = 0; }
    var messages = getMessagesForLocale(customLocale || locale || DEFAULT_LOCALE);
    var message = messages[id];
    // Fallback if key not translated
    if (!message) {
        return id;
    }
    // Plural
    if (typeof message === "object" && message !== null) {
        var pluralStrings = ["zero", "one", "many"];
        // INFO: Get absolute value to fix negative indexes
        var pluralString = pluralStrings[Math.min(pluralStrings.length - 1, Math.abs(pluralIndex))];
        message = message[pluralString];
        if (!message) {
            return id;
        }
    }
    if (typeof message !== "string") {
        return id;
    }
    return message;
};
export var getLocalizedText = function (id, values, pluralIndex, customLocale) {
    if (values === void 0) { values = {}; }
    if (pluralIndex === void 0) { pluralIndex = 0; }
    var message = getMessageForId(id, pluralIndex, customLocale);
    // Replace translation for values
    if (values) {
        Object.keys(values).forEach(function (value) {
            var regex = new RegExp("{{".concat(value, "}}"), "g");
            message = message.replace(regex, values[value]);
        });
    }
    return message;
};
/**
 * Label for an enum value that may not be there, for summary rendering.
 *
 * A `formatSummary` callback cannot assume its value exists: two of the four
 * summary implementations (this package's FormSummary and summary.utils'
 * mapField) only honour the STEP-level `showIf` and then format every field
 * with data, hidden ones included - and a field hidden while its step was
 * submitted has no stored value at all. Returns undefined so the summary simply
 * shows nothing, rather than throwing or printing a raw key.
 */
export var getOptionalLocalizedEnumText = function (params) {
    var prefix = params.prefix, value = params.value, customLocale = params.customLocale;
    return value
        ? getLocalizedText("".concat(prefix, ".").concat(value.toLowerCase()), {}, 0, customLocale)
        : undefined;
};
// Feedback messages
var sanitize = function (input) { return capitalize(input.toLowerCase()); };
var getReadableModel = function (modelType) {
    return getLocalizedText("system.model.".concat(stringToSnakeCase(modelType)).toLowerCase());
};
export var getGeneralCreateSuccessText = function (type) {
    return sanitize(getLocalizedText("system.create.success", {
        type: getReadableModel(type),
    }));
};
export var getGeneralCreateSuccessGoToDetailText = function (type) {
    return sanitize(getLocalizedText("system.create.success.cta_detail", {
        type: getReadableModel(type),
    }));
};
export var getGeneralCreateFailureText = function (type) {
    return sanitize(getLocalizedText("system.create.failure", {
        type: getReadableModel(type),
    }));
};
export var getGeneralUpdateSuccessText = function (type) {
    return sanitize(getLocalizedText("system.update.success", {
        type: getReadableModel(type),
    }));
};
export var getGeneralUpdateFailureText = function (type) {
    return sanitize(getLocalizedText("system.update.failure", {
        type: getReadableModel(type),
    }));
};
export var getGeneralRemoveSuccessText = function (type) {
    return sanitize(getLocalizedText("system.delete.success", {
        type: getReadableModel(type),
    }));
};
export var getGeneralRemoveFailureText = function (type) {
    return sanitize(getLocalizedText("system.delete.failure", {
        type: getReadableModel(type),
    }));
};
export var getGeneralArchiveSuccessText = function (type) {
    return sanitize(getLocalizedText("system.archive.success", {
        type: getReadableModel(type),
    }));
};
export var getGeneralArchiveFailureText = function (type) {
    return sanitize(getLocalizedText("system.archive.failure", {
        type: getReadableModel(type),
    }));
};
export var getGeneralUnarchiveSuccessText = function (type) {
    return sanitize(getLocalizedText("system.unarchive.success", {
        type: getReadableModel(type),
    }));
};
export var getGeneralUnarchiveFailureText = function (type) {
    return sanitize(getLocalizedText("system.unarchive.failure", {
        type: getReadableModel(type),
    }));
};
